import { useRef } from 'react'
import { createPortal } from 'react-dom'
import CloseButton from './buttons/CloseButton'
import useBodyScrollLock from '../../../hooks/useBodyScrollLock';
import { CSSTransition } from 'react-transition-group';
import classNames from 'classnames';
import { t } from '../../../utilities/translator';
import Button from '../../../components/button/Button';
import FadeTransition from '../../../components/animations/FadeTransition';

interface CustomizeModalProps {
  show: boolean;
  slideDirection?: 'vertical' | 'horizontal';
  children: React.ReactNode;
  className?: string;
  close: () => void;
}

const CustomizeModal = ({
  show,
  slideDirection = 'vertical',
  children,
  className = '',
  close,
}: CustomizeModalProps) => {
  const nodeRef = useRef(null);
  useBodyScrollLock({ condition: show, mobileOnly: true });
  let portal = document.getElementById('modal-portal');

  if (!portal) {
    portal = document.createElement('div');
    portal.id = 'modal-portal';
    document.body.appendChild(portal);
  }

  const modal = (
    <CSSTransition
      nodeRef={nodeRef}
      in={show}
      timeout={250}
      classNames={`slide-${slideDirection}`}
      unmountOnExit
    >
      <div className="panel-container">
        <FadeTransition in={show}>
          <div className="panel-overlay" onClick={close} />
        </FadeTransition>
        <div className={classNames('customize-panel text-primary', className)} ref={nodeRef}>
          {children}
        </div>
      </div>
    </CSSTransition>
  );

  return createPortal(modal, portal);
}

const Header = ({
  close,
  titleLabel,
  titleIcon,
  children
}: {
  close: () => void;
  titleLabel: string;
  titleIcon: string;
  children: React.ReactNode;
}) => {
  return (
    <div className="panel-header pt-13">
      <div className="panel-header__title">
        <div className="panel-title gap-2 heading-small">
          <i className={titleIcon} />
          {titleLabel}
        </div>
        <CloseButton onClick={close} type="alternative" />
      </div>
      {children}
    </div>
  )
}

const Body = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  return (
    <div className={classNames('panel-body px-7 gap-6', className)}>
      {children}
    </div>
  )
}

const Footer = ({ close, onClear, onSave, disableSave = false, children }: {
  close: () => void;
  onClear: () => void;
  onSave: () => void;
  disableSave?: boolean;
  children?: React.ReactNode;
}) => {
  return (
    <div className="panel-footer px-5 pb-5 gap-3">
      {!children && (
        <>
          <Button
            onClick={close}
            label={<i className="ri-arrow-left-s-line rtl:-scale-x-100 text-lg" />}
            size="lg"
            type="secondary"
            className="panel-button panel-button__back"
            rounded
          />
          <Button
            onClick={onClear}
            label={t('buttons.Clear')}
            size="lg"
            type="secondary"
          />
          <Button
            onClick={onSave}
            label={t('buttons.Save')}
            size="lg"
            type="primary"
            disabled={disableSave}
          />
        </>
      )}
      {children}
    </div>
  )
}

CustomizeModal.Header = Header;
CustomizeModal.Body = Body;
CustomizeModal.Footer = Footer;

export default CustomizeModal
